<template>
  <div class="progress-indicator">
    <p
      class="progress-indicator__text text-body-s-regular"
      v-text="progressText"
    />
    <div class="progress-indicator__bar-background">
      <div
        class="progress-indicator__bar"
        :style="{ width: `${progress}%` }"
      />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Progress Indicator (progress-indicator)
 * -----------------------------------------------------------------------------
 * Indicator to show progress towards a given threshold.
 *
 * @param {Number} threshold - The progress threshold as a number.
 * @param {Number} cartTotal - The total value of the cart.
 * @param {Boolean} isSpecialCondition - Whether or not to show a 3rd message
 * @param {String} thresholdMetMessage - The message to show when the
 * threshold is met.
 * @param {String} thresholdUnmetMessage - The message to show when the
 *  threshold is not met.
 * @param {String} specialConditionMessage - The message to show when
 * the threshold is met and the special condition is true.
 *
 */
export default {
  name: 'ProgressIndicator',

  props: {
    threshold: {
      type: Number,
      default: 0,
      required: true,
    },
    total: {
      type: Number,
      default: 0,
      required: true,
    },
    isSpecialCondition: {
      type: Boolean,
      default: false,
    },
    thresholdMetMessage: {
      type: String,
      default: 'Met',
    },
    thresholdUnmetMessage: {
      type: String,
      default: 'Unmet',
    },
    specialConditionMessage: {
      type: String,
      default: 'Special message',
    },
  },
  emits: {
    'threshold-met': (payload) => typeof payload === 'boolean',
  },

  computed: {

    /**
     * Progress percentage.
     * @return {Number}
     */
    progress() {
      const progress = (this.total / this.threshold) * 100
      return Math.min(100, progress)
    },

    /**
     * Remaining amount to reach threshold.
     * @return {Number}
     */
    remaining() {
      return this.threshold - this.total
    },

    /**
     * Progress text to display.
     * @return {String}
     */
    progressText() {
      if (this.progress === 100) {
        return this.isSpecialCondition ? this.specialConditionMessage : this.thresholdMetMessage
      }
      return this.thresholdUnmetMessage.replace(
        '{{ amount }}',
        this.$formatMoney(
          this.remaining,
          this.$variable('store.preferredMoneyFormat'),
        ),
      )
    },

  },
  watch: {
    progress() {
      this.emitProgress()
    },
  },
  mounted() {
    this.emitProgress()
  },
  methods: {

    /**
     * Emit progress event.
     */
    emitProgress() {
      this.$emit('threshold-met', this.progress === 100)
    },
  },

}
</script>

<style lang="scss">
@import './progress-indicator';
</style>
